<script setup lang="ts">
import type { LandingResponse } from '@/types/landing'
import * as BviLib from 'bvi/dist/js/bvi.min.js'
import { inject, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import LangIcon from '@/assets/images/lang-icon.svg?component'
import LocationIcon from '@/assets/images/location-icon.svg?component'
import logoImg from '@/assets/images/logo.png'
import MailIcon from '@/assets/images/mail-icon-2.svg?component'
import PhoneIcon from '@/assets/images/phone-icon.svg?component'
import whatsappImg from '@/assets/images/whatsapp.png'
import UiContainer from '@/components/landing/ui/UiContainer.vue'

const route = useRoute()
const isMenuOpen = ref(false)
const contentData = inject<LandingResponse | null>('contentData')

const navLinks = [
  { name: 'Синдром красного глаза', to: '/red-eye' },
  {
    name: 'Сетчатка',
    to: '/retina',
    children: [
      { name: 'Диабетический макулярный отек', to: '/dmo' },
      { name: 'Возрастная макулярная дегенерация', to: '/vmd' },
      { name: 'Ретинопатия недоношенных', to: '/rn' },
    ],
  },
  { name: 'Глаукома', to: '/glaucoma' },
  { name: 'Миопия', to: '/myopia' },
  { name: 'Документы', to: '/documents' },
]

function toggleMenu() {
  isMenuOpen.value = !isMenuOpen.value
}

watch(() => route.path, () => {
  isMenuOpen.value = false
})

onMounted(() => {
  // eslint-disable-next-line no-new
  new BviLib.Bvi({
    target: '.bvi-open',
    fontSize: 26,
    theme: 'white',
    speech: false,
    panelFixed: true,
    reload: false,
  })
})
</script>

<template>
  <header class="the-header">
    <UiContainer>
      <div class="the-header__inner">
        <RouterLink :to="{ name: 'landingHome' }" class="the-header__logo">
          <img :src="logoImg" alt="Ключ к диагнозу" class="the-header__logo-img">
        </RouterLink>
        <RouterLink to="/documents" class="link the-header__doc-link">
          Документы
        </RouterLink>

        <nav class="the-header__nav" :class="{ 'the-header__nav--open': isMenuOpen }">
          <template v-for="link in navLinks" :key="link.to">
            <div v-if="link.children" class="the-header__nav-group">
              <RouterLink :to="link.to" class="the-header__nav-link">
                {{ link.name }}
              </RouterLink>
              <div class="the-header__subnav">
                <RouterLink
                  v-for="child in link.children" :key="child.to" :to="child.to"
                  class="the-header__subnav-link"
                >
                  {{ child.name }}
                </RouterLink>
              </div>
            </div>
            <RouterLink v-else :to="link.to" class="link the-header__nav-link">
              {{ link.name }}
            </RouterLink>
          </template>
          <button class="bvi-open the-header__nav-button">
            <LangIcon class="the-header__lang" />
            Режим для слабовидящих
          </button>
        </nav>

        <div class="the-header__contacts">
          <div class="the-header__contact-group">
            <a :href="`tel:${contentData?.contacts.phones[0]}`" class="link the-header__contact-row">
              <PhoneIcon class="the-header__icon" />
              <span class="the-header__phone">{{ contentData?.contacts.phones[0] }}</span>
            </a>
            <div class="the-header__contact-row link">
              <!-- <img :src="locationIcon" alt="" class="the-header__icon"> -->
              <LocationIcon class="the-header__icon" />
              <a href="https://yandex.by/maps/-/CPgEaG8t" target="_blank" class="the-header__address">{{
                contentData?.contacts.addresses[0]
              }}</a>
            </div>
          </div>

          <a :href="contentData?.contacts.maxLinks[0] || '#'">
            <img :src="whatsappImg" alt="MAX" class="the-header__whatsapp">
          </a>

          <div v-if="contentData?.contacts.emails" class="the-header__email-group">
            <div class="the-header__contact-row">
              <!-- <img :src="mailIcon" alt="" class="the-header__icon the-header__icon--sm"> -->
              <MailIcon class="the-header__icon the-header__icon--sm" />
              <span class="the-header__email-label">Электронная почта</span>
            </div>
            <a
              v-for="email of contentData?.contacts.emails" :key="email" :href="`mailto:${email}`"
              class="link the-header__email"
            >{{ email }}</a>
          </div>

          <button title="Режим для слабовидящих" class="bvi-open">
            <LangIcon class="the-header__lang" />
          </button>
        </div>

        <button
          class="the-header__burger" :class="{ 'the-header__burger--active': isMenuOpen }" aria-label="Меню"
          @click="toggleMenu"
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </UiContainer>
  </header>
</template>

<style scoped lang="scss">
.the-header {
  position: sticky;
  top: 0;
  z-index: 100;
  background-color: $color-bg;
  box-shadow: $shadow-header;

  &__inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 6rem;
    gap: $spacing-lg;
  }

  &__logo {
    flex-shrink: 0;
  }

  &__logo-img {
    height: 4.25rem;
    width: auto;
    mix-blend-mode: multiply;

    @media (max-width: $breakpoint-sm) {
      height: 3rem;
    }
  }

  &__doc-link {
    display: none;
    font-size: $font-size-xl;
    font-weight: $font-weight-medium;
    color: $color-text;
    white-space: nowrap;
    transition: color $transition-fast;

    @include xl {
      display: block;
    }
  }

  &__nav {
    display: none;

    &--open {
      display: flex;
      flex-direction: column;
      position: absolute;
      top: 6rem;
      left: 0;
      right: 0;
      background-color: $color-bg;
      padding: $spacing-lg;
      box-shadow: $shadow-md;
      z-index: 99;
      gap: $spacing-sm;
      max-height: calc(100vh - 6rem);
      overflow-y: auto;

    }
  }

  &__nav-link {
    font-size: $font-size-lg;
    font-weight: $font-weight-medium;
    color: $color-text;
    white-space: nowrap;
    padding: $spacing-xs 0;
    transition: color $transition-fast;

    &.router-link-active {
      color: $color-primary;
    }
  }

  &__nav-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  &__nav-button {
    display: flex;
    gap: 1rem;
    font-size: $font-size-lg;
    font-weight: $font-weight-medium;
    color: $color-text;
    margin-top: 1rem;
  }

  &__subnav {
    display: flex;
    flex-direction: column;
    padding: 0 0 0 $spacing-lg;
    gap: $spacing-xs;
  }

  &__subnav-link {
    font-size: $font-size-base;
    font-weight: $font-weight-normal;
    color: $color-text;
    padding: $spacing-sm $spacing-md;
    border-radius: $radius-sm;
    transition: background-color $transition-fast, color $transition-fast;
    white-space: nowrap;

    &:hover {
      background-color: $color-bg-light;
      color: $color-primary !important;
    }

    &.router-link-active {
      color: $color-primary;
    }
  }

  &__contacts {
    display: none;

    @include xl {
      display: flex;
      align-items: center;
      gap: $spacing-lg;
    }
  }

  &__contact-group {
    display: flex;
    flex-direction: column;
    gap: $spacing-sm;
  }

  &__contact-row {
    display: flex;
    align-items: center;
    gap: $spacing-sm;

    &.link {
      &:hover {
        .the-header__phone {
          color: $color-primary !important;
        }

        .the-header__address {
          opacity: .7;
          color: unset !important;
        }
      }
    }
  }

  &__icon {
    width: 1.5rem;
    height: 1.5rem;
    flex-shrink: 0;
  }

  &__icon--sm {
    width: 1.18rem;
    height: 1.10rem;
  }

  &__phone {
    font-size: $font-size-xl;
    font-weight: $font-weight-medium;
    color: $color-text;
    transition: color $transition-fast;
  }

  &__address {
    font-size: $font-size-xl;
    font-weight: $font-weight-semibold;
    color: $color-primary;
    transition: opacity $transition-fast;
  }

  &__whatsapp {
    width: 2.375rem;
    height: 2.375rem;
    flex-shrink: 0;
  }

  &__email-group {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  &__email-label {
    font-size: $font-size-base;
    font-weight: $font-weight-medium;
    color: $color-primary;
  }

  &__email {
    font-size: $font-size-xl;
    font-weight: $font-weight-medium;
    color: $color-text;
    text-decoration: underline;
  }

  &__lang {
    width: 1.5rem;
    height: 1.2rem;
    flex-shrink: 0;
    cursor: pointer;
  }

  &__burger {
    display: flex;
    flex-direction: column;
    gap: 5px;
    padding: $spacing-sm;

    @include xl {
      display: none;
    }

    span {
      display: block;
      width: 1.5rem;
      height: 2px;
      background-color: $color-text;
      border-radius: 1px;
      transition: all $transition-fast;
    }

    &--active {
      span:nth-child(1) {
        transform: rotate(45deg) translate(5px, 5px);
      }

      span:nth-child(2) {
        opacity: 0;
      }

      span:nth-child(3) {
        transform: rotate(-45deg) translate(5px, -5px);
      }
    }
  }
}
</style>
